import { useState, useEffect } from 'preact/hooks';

interface DevSlotInfo {
  slot: 'a' | 'b' | 'c';
  port: number;
  url: string;
}

interface GithubRepoOption {
  full_name: string;
  name: string;
  private: boolean;
  language: string | null;
  pushed_at: string;
}

interface Props {
  provisioningStatus: string;
  onComplete: () => void;
  socket: any | null;
  /** Whether the user already has GitHub connected */
  githubConnected?: boolean;
  /** GitHub login (for display) */
  githubLogin?: string;
  /** Profile flag: whether GitHub OAuth is available on this deployment */
  githubOauthEnabled?: boolean;
  /** Brand id (e.g. 'protofac') — drives the BYO-Claude-subscription onboarding copy + the
   *  `claude login` authenticate step. Protofac users run the agent on their OWN Anthropic
   *  subscription, so the one-time `claude login` is the make-or-break onboarding action. */
  brandId?: string;
  /** Whether Gemini is enabled on this profile. false on protofac/valko → drop Gemini mentions
   *  from the onboarding copy (invariant: Protofac is Claude-only). */
  geminiEnabled?: boolean;
}

type WizardStep = 'welcome' | 'github_connect' | 'repo_select' | 'provisioning' | 'ready' | 'authenticate' | 'error';

export function OnboardingWizard({
  provisioningStatus: initialStatus,
  onComplete,
  socket,
  githubConnected = false,
  githubLogin,
  githubOauthEnabled = true,
  brandId,
  geminiEnabled = true,
}: Props) {
  // Protofac (and any Claude-only brand) onboards onto the user's OWN Claude subscription, so it
  // gets the guided `claude login` authenticate step + credit-model framing. Gemini-off brands
  // drop the "Gemini CLI" mention from the workspace copy.
  const isProtofac = brandId === 'protofac';
  const claudeOnly = geminiEnabled === false;
  const preinstalledCopy = claudeOnly ? 'Claude Code' : 'Claude Code and Gemini CLI';
  const [copied, setCopied] = useState(false);

  const copyLoginCmd = () => {
    navigator.clipboard?.writeText('claude login').then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
      () => { /* clipboard blocked — the command is shown inline anyway */ },
    );
  };
  const [step, setStep] = useState<WizardStep>(
    initialStatus === 'provisioning' ? 'provisioning' :
    initialStatus === 'ready' ? 'ready' :
    initialStatus === 'error' ? 'error' : 'welcome',
  );
  const [sandboxName, setSandboxName] = useState('');
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [devSlots, setDevSlots] = useState<DevSlotInfo[]>([]);
  const [repos, setRepos] = useState<GithubRepoOption[]>([]);
  const [selectedRepos, setSelectedRepos] = useState<Set<string>>(new Set());
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [cloneProgress, setCloneProgress] = useState<Record<string, 'cloning' | 'done' | 'error'>>({});

  const loadRepos = async () => {
    setLoadingRepos(true);
    try {
      const res = await fetch('/api/workspace/github-repos', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json() as { repos: GithubRepoOption[] };
        setRepos(data.repos ?? []);
        // Pre-select first 5 repos
        const top5 = (data.repos ?? []).slice(0, 5).map(r => r.full_name);
        setSelectedRepos(new Set(top5));
      }
    } catch {
      // Non-fatal — skip repo selection
    } finally {
      setLoadingRepos(false);
    }
  };

  // Listen for WebSocket provisioning events
  useEffect(() => {
    if (!socket) return;

    const onProgress = (data: { step: string; message: string; progress: number }) => {
      setStep('provisioning');
      setProgress(data.progress);
      setProgressMessage(data.message);
    };
    const onReady = (data: { machineName?: string; devSlots?: DevSlotInfo[] }) => {
      if (data.devSlots) setDevSlots(data.devSlots);
      // If GitHub is connected and user is on provisioning step → offer repo selection
      if (githubConnected) {
        loadRepos().then(() => setStep('repo_select'));
      } else {
        setStep('ready');
      }
      setProgress(100);
      setSubmitting(false);
    };
    const onError = (data: { error: string }) => {
      setStep('error');
      setError(data.error);
      setSubmitting(false);
    };
    const onCloneProgress = (data: { repo: string; status: 'cloning' | 'done' | 'error' }) => {
      setCloneProgress(prev => ({ ...prev, [data.repo]: data.status }));
    };

    socket.on('workspace:provision_progress', onProgress);
    socket.on('workspace:ready', onReady);
    socket.on('workspace:error', onError);
    socket.on('workspace:clone_progress', onCloneProgress);

    return () => {
      socket.off('workspace:provision_progress', onProgress);
      socket.off('workspace:ready', onReady);
      socket.off('workspace:error', onError);
      socket.off('workspace:clone_progress', onCloneProgress);
    };
  }, [socket, githubConnected]);

  const startProvisioning = async () => {
    const name = sandboxName.trim();
    if (!name) return;
    setSubmitting(true);
    setStep('provisioning');
    setProgress(5);
    setProgressMessage('Starting provisioning…');

    try {
      const res = await fetch('/api/workspace/provision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sandboxName: name }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Request failed' }));
        throw new Error((err as any).message || `HTTP ${res.status}`);
      }
    } catch (err: any) {
      setStep('error');
      setError(err.message);
      setSubmitting(false);
    }
  };

  const cloneSelectedRepos = async () => {
    if (selectedRepos.size === 0) {
      setStep('ready');
      return;
    }
    setCloning(true);
    try {
      await fetch('/api/workspace/clone-repos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ repos: Array.from(selectedRepos) }),
      });
    } catch {
      // Fire-and-forget — progress comes via WebSocket
    }
    // Transition to ready after starting clone (progress shows via events)
    setStep('ready');
  };

  const toggleRepo = (fullName: string) => {
    setSelectedRepos(prev => {
      const next = new Set(prev);
      if (next.has(fullName)) next.delete(fullName);
      else next.add(fullName);
      return next;
    });
  };

  const retry = () => {
    setError('');
    setSubmitting(false);
    setProgress(0);
    setProgressMessage('');
    setStep('welcome');
  };

  const connectGithub = () => {
    // GitHub OAuth connect — redirects back to app with ?github_connected=1
    window.location.href = '/api/github?redirectUri=' + encodeURIComponent(window.location.href);
  };

  return (
    <div class="onboarding-overlay">
      <div class="onboarding-card">

        {/* Step 1: Welcome */}
        {step === 'welcome' && (
          <>
            <div class="onboarding-icon">🚀</div>
            <h2>Let's set up your workspace</h2>
            <p class="onboarding-subtitle">
              We'll create a personal sandbox with {preinstalledCopy} pre-installed.
            </p>
            <div class="onboarding-field">
              <label for="sandbox-name">Workspace name</label>
              <input
                id="sandbox-name"
                type="text"
                value={sandboxName}
                onInput={(e) => setSandboxName((e.target as HTMLInputElement).value)}
                placeholder="e.g. my-workspace"
                pattern="[a-zA-Z0-9-]+"
                maxLength={40}
                disabled={submitting}
                autoFocus
              />
              <span class="field-hint">Letters, numbers, and hyphens only</span>
            </div>

            {/* GitHub CTA */}
            {githubOauthEnabled && (
              <div class="onboarding-github-cta">
                {githubConnected ? (
                  <div class="github-connected-badge">
                    GitHub connected as @{githubLogin ?? 'you'} — repos will be cloned automatically
                  </div>
                ) : (
                  <div class="github-connect-prompt">
                    <button class="onboarding-btn secondary github-btn" onClick={connectGithub}>
                      Connect GitHub for instant repo setup
                    </button>
                    <span class="field-hint">Optional — connect later in Settings</span>
                  </div>
                )}
              </div>
            )}

            <button
              class="onboarding-btn primary"
              onClick={startProvisioning}
              disabled={!sandboxName.trim() || submitting}
            >
              Set Up My Workspace
            </button>
          </>
        )}

        {/* Step 2: Provisioning progress */}
        {step === 'provisioning' && (
          <>
            <div class="onboarding-icon spin">⚙️</div>
            <h2>Setting up your workspace…</h2>
            <div class="progress-bar">
              <div class="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <p class="progress-message">{progressMessage || 'Please wait…'}</p>
            <p class="onboarding-note">This usually takes 15–30 seconds.</p>
          </>
        )}

        {/* Step 3: Repo selection (GitHub connected) */}
        {step === 'repo_select' && (
          <>
            <div class="onboarding-icon">📦</div>
            <h2>Clone your repos</h2>
            <p class="onboarding-subtitle">
              Select repos to clone into your workspace. You can add more later.
            </p>
            {loadingRepos ? (
              <p class="onboarding-note">Loading repos…</p>
            ) : repos.length === 0 ? (
              <p class="onboarding-note">No repos found — you can clone manually from the terminal.</p>
            ) : (
              <div class="repo-list">
                {repos.map(repo => (
                  <label key={repo.full_name} class="repo-item">
                    <input
                      type="checkbox"
                      checked={selectedRepos.has(repo.full_name)}
                      onChange={() => toggleRepo(repo.full_name)}
                    />
                    <span class="repo-name">{repo.name}</span>
                    {repo.private && <span class="repo-badge private">private</span>}
                    {repo.language && <span class="repo-badge lang">{repo.language}</span>}
                  </label>
                ))}
              </div>
            )}
            <div class="onboarding-actions">
              <button
                class="onboarding-btn primary"
                onClick={cloneSelectedRepos}
                disabled={cloning}
              >
                {selectedRepos.size > 0 ? `Clone ${selectedRepos.size} repo${selectedRepos.size !== 1 ? 's' : ''}` : 'Skip'}
              </button>
              <button class="onboarding-btn text" onClick={() => setStep('ready')}>
                Skip for now
              </button>
            </div>
          </>
        )}

        {/* Step 4: Ready */}
        {step === 'ready' && (
          <>
            <div class="onboarding-icon">✅</div>
            <h2>Your workspace is ready!</h2>
            <p>
              <strong>{sandboxName || 'workspace'}</strong> is running with {preinstalledCopy} pre-installed.
            </p>

            {/* Dev slot URLs */}
            {devSlots.length > 0 && (
              <div class="dev-slots">
                <p class="dev-slots-label">Dev preview URLs:</p>
                {devSlots.map(slot => (
                  <a
                    key={slot.slot}
                    href={slot.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="dev-slot-link"
                  >
                    {slot.slot.toUpperCase()} — {slot.url}
                  </a>
                ))}
              </div>
            )}

            {/* Clone progress (if cloning in background) */}
            {Object.keys(cloneProgress).length > 0 && (
              <div class="clone-progress-list">
                {Object.entries(cloneProgress).map(([repo, status]) => (
                  <div key={repo} class={`clone-item clone-${status}`}>
                    {status === 'cloning' ? '⏳' : status === 'done' ? '✓' : '✗'} {repo}
                  </div>
                ))}
              </div>
            )}

            {isProtofac ? (
              <>
                <p class="onboarding-note">
                  One more step: connect your Claude subscription so the sandbox runs on <strong>your own</strong> Anthropic credits.
                </p>
                <button class="onboarding-btn primary" onClick={() => setStep('authenticate')}>
                  Connect Claude →
                </button>
              </>
            ) : (
              <>
                <p class="onboarding-note">
                  Open the terminal (SSH button in the toolbar) to authenticate your AI subscriptions.
                </p>
                <button class="onboarding-btn primary" onClick={onComplete}>
                  Start Coding
                </button>
              </>
            )}
          </>
        )}

        {/* Step 5: Authenticate — one-time `claude login` (Protofac BYO-subscription) */}
        {step === 'authenticate' && (
          <>
            <div class="onboarding-icon">🔑</div>
            <h2>Connect your Claude subscription</h2>
            <p class="onboarding-subtitle">
              Your sandbox runs Claude Code under <strong>your own</strong> Anthropic subscription —
              your monthly Agent-SDK credits pay for the work. We never bill you for the AI.
            </p>

            <ol class="onboarding-steps">
              <li>Open the <strong>terminal</strong> (the ⌨ SSH button in the toolbar).</li>
              <li>
                Run this once:
                <div class="onboarding-cmd">
                  <code>claude login</code>
                  <button type="button" class="onboarding-cmd-copy" onClick={copyLoginCmd}>
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              </li>
              <li>A sign-in link appears — tap it to authorize in your browser, paste the code back, and you're done.</li>
            </ol>

            <p class="onboarding-note">
              Then point the <strong>Plans</strong> tab at your project's folder in
              <strong> Settings → Workspace</strong>, and just start talking.
            </p>
            <button class="onboarding-btn primary" onClick={onComplete}>
              Start
            </button>
            <button class="onboarding-btn text" onClick={onComplete}>
              I'll do this later
            </button>
          </>
        )}

        {/* Error */}
        {step === 'error' && (
          <>
            <div class="onboarding-icon">❌</div>
            <h2>Something went wrong</h2>
            <p class="error-text">{error || 'Provisioning failed. Please try again.'}</p>
            <button class="onboarding-btn" onClick={retry}>Try Again</button>
          </>
        )}
      </div>
    </div>
  );
}
